import React, { useState } from 'react';
import WelcomeMessage from '../components/WelcomeMessage';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import { SQUARE_CONFIG, getSquareSDKUrl } from '../config/square';

const GroundSchool = () => {
  const { currentUser } = useApp();
  const [date, setDate] = useState(new Date());
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    comment: ''
  });
  const [eventFormData, setEventFormData] = useState({
    name: '',
    topic: '',
    startDateTime: '',
    endDateTime: '',
    description: '',
    locationPhysical: '',
    locationOnline: '',
    capacity: '',
    price: '',
    url: '',
    recordingUrl: ''
  });
  const [eventMessage, setEventMessage] = useState('');
  const [registrationMessage, setRegistrationMessage] = useState('');
  const [activeEventTab, setActiveEventTab] = useState('event');
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userMessage, setUserMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [showParticipantsDialog, setShowParticipantsDialog] = useState(false);
  const [selectedEventForParticipants, setSelectedEventForParticipants] = useState(null);
  const [view, setView] = useState('month');
  const [showEventGroupForm, setShowEventGroupForm] = useState(false);
  const [eventGroupFormData, setEventGroupFormData] = useState({
    name: '',
    description: '',
    daysOfWeek: [],
    recurrenceType: 'weeks',
    recurrenceInterval: 1,
    occurrenceCount: 10,
    startDate: '',
    endDate: '',
    startTime: '09:00',
    endTime: '11:00',
    price: '',
    eventPrice: '',
    capacity: '',
    topic: '',
    locationPhysical: '',
    locationOnline: '',
    url: ''
  });
  const [eventGroupMessage, setEventGroupMessage] = useState('');
  const [editEventGroupData, setEditEventGroupData] = useState(null);
  const [registrationEventGroupData, setRegistrationEventGroupData] = useState(null);
  const [activeRegistrationTab, setActiveRegistrationTab] = useState('event');
  const [squareLoaded, setSquareLoaded] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [cardInstance, setCardInstance] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [isRegistered, setIsRegistered] = useState({ event: false, eventseries: false });
  const [paymentFormInitialized, setPaymentFormInitialized] = useState(false);

  const getEventSeriesPrice = () => {
    if (!registrationEventGroupData?.price) return 0;
    const basePrice = parseFloat(registrationEventGroupData.price);
    if (registrationEventGroupData.discountPercent && registrationEventGroupData.discountPercent > 0) {
      return Math.round(basePrice * (1 - registrationEventGroupData.discountPercent / 100) * 100) / 100;
    }
    return basePrice;
  };

  const fetchRegistrationEventGroupData = async (groupId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/event-group/reservation/${groupId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const groupData = await response.json();
        
        // Check if current user has a reservation and is paid
        const userReservation = groupData.reservation && groupData.reservation.userId === currentUser?.user?.id ? groupData.reservation : null;
        const seriesRegistered = !!userReservation;
        const seriesPaid = userReservation && userReservation.isPaid;
        
        setIsRegistered(prev => ({ ...prev, eventseries: seriesRegistered }));
        
        if (seriesPaid) {
          const paymentData = {
            isPaid: true,
            amountPaid: userReservation.amountPaid,
            receiptNumber: userReservation.receiptNumber,
            receiptUrl: userReservation.receiptUrl,
            tab: 'eventseries'
          };

          setPaymentInfo(paymentData);
        } else if (!paymentInfo || paymentInfo.tab !== 'event') {
          setPaymentInfo(null);
        }
        
        setRegistrationEventGroupData(groupData);
      } else {
        const errorText = await response.text();
        setRegistrationEventGroupData({ error: `Failed to load event series: ${errorText}` });
      }
    } catch (error) {
      setRegistrationEventGroupData({ error: `Error loading event series: ${error.message}` });
    }
  };

  const fetchEditEventGroupData = async (groupId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/event-group/${groupId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const groupData = await response.json();
        setEditEventGroupData(groupData);
      }
    } catch (error) {
    }
  };

  React.useEffect(() => {
    // Check if Square SDK is already loaded
    if (window.Square) {
      setSquareLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = getSquareSDKUrl();
    script.async = true;
    script.onload = () => {
      setSquareLoaded(true);
    };
    script.onerror = (error) => {
      setSquareLoaded(false);
    };
    
    // Check if script is already in the document
    const existingScript = document.querySelector(`script[src="${getSquareSDKUrl()}"]`);
    if (!existingScript) {
      document.head.appendChild(script);
    } else {
      setSquareLoaded(true);
    }
    
    const timeout = setTimeout(() => {
      if (!window.Square) {
        setSquareLoaded(false);
      }
    }, 5000);
    
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  // Initialize Square payment form when payment form is shown
  React.useEffect(() => {
    if (showPaymentForm && squareLoaded && !paymentFormInitialized && !cardInstance) {
      const initializePaymentForm = async () => {
        try {
          if (!window.Square) {
            return;
          }

          const payments = window.Square.payments(SQUARE_CONFIG.applicationId, SQUARE_CONFIG.locationId);
          const card = await payments.card();
          
          // Wait for the card container to be available in the DOM
          const cardContainer = document.getElementById('card-container');
          if (!cardContainer) {
            return;
          }

          await card.attach('#card-container');
          setCardInstance(card);
          setPaymentFormInitialized(true);
        } catch (error) {
          setPaymentFormInitialized(false);
        }
      };

      // Small delay to ensure DOM is ready
      const timer = setTimeout(initializePaymentForm, 200);
      return () => clearTimeout(timer);
    }
  }, [showPaymentForm, squareLoaded, paymentFormInitialized, cardInstance]);

  React.useEffect(() => {
    if (!currentUser?.sessionId) return;
    
    // Calculate date range: 7 days before first day of month to 7 days after last day of month
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const fromDate = new Date(firstDay);
    fromDate.setDate(firstDay.getDate() - 7);
    
    const toDate = new Date(lastDay);
    toDate.setDate(lastDay.getDate() + 7);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const fromDateStr = formatDate(fromDate);
    const toDateStr = formatDate(toDate);
    

    fetch(`${API_BASE_URL}/event/list/${fromDateStr}/${toDateStr}`, {
      headers: {
        'session-id': currentUser.sessionId
      }
    })
      .then(response => response.json())
      .then(data => {
        setEvents(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        setEvents([]);
      });
  }, [currentUser?.sessionId, date]);

  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());
    
    const dates = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      dates.push(day);
    }
    return dates;
  };

  const getEventsForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const filteredEvents = events.filter(event => {
      if (!event.startDateTime) return false;
      const eventDateObj = new Date(event.startDateTime);
      if (isNaN(eventDateObj.getTime())) return false;
      const eventYear = eventDateObj.getFullYear();
      const eventMonth = String(eventDateObj.getMonth() + 1).padStart(2, '0');
      const eventDay = String(eventDateObj.getDate()).padStart(2, '0');
      const eventDateStr = `${eventYear}-${eventMonth}-${eventDay}`;
      return eventDateStr === dateStr;
    });
    

    
    return filteredEvents;
  };

  const formatTime = (datetime) => {
    if (!datetime) return 'Invalid time';
    
    // Ensure we have a proper UTC datetime string
    let dateStr = datetime;
    if (typeof datetime === 'string' && !datetime.endsWith('Z') && !datetime.includes('+')) {
      dateStr = datetime + 'Z'; // Add Z if missing to indicate UTC
    }
    
    const utcDate = new Date(dateStr);
    if (isNaN(utcDate.getTime())) return 'Invalid time';
    
    return utcDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true
    });
  };

  const handleEventClick = async (event, date) => {
    setSelectedEvent({ ...event, date });
    setFormData({
      firstName: currentUser?.user?.firstName || currentUser?.firstName || currentUser?.first_name || '',
      lastName: currentUser?.user?.lastName || currentUser?.lastName || currentUser?.last_name || '',
      email: currentUser?.user?.email || currentUser?.email || '',
      phone: currentUser?.user?.phone || currentUser?.phone || '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      comment: ''
    });
    
    setRegistrationEventGroupData(null);
    setActiveRegistrationTab(event.groupId ? 'eventseries' : 'event');
    setShowPaymentForm(false);
    
    // Fetch event series data if defaulting to eventseries tab
    if (event.groupId) {
      fetchRegistrationEventGroupData(event.groupId);
    }
    
    // Check if user is already registered and paid
    // For clients, participantList contains only their own participant object if registered
    // For staff/admin/superuser, participantList contains all participants
    const userRegistration = event.participantList?.find(p => p.userId === currentUser?.user?.id);
    const eventRegistered = !!userRegistration;
    const eventPaid = userRegistration && userRegistration.isPaid;
    
    // If user has paid for series, they're also registered for individual events
    const seriesPaid = paymentInfo && paymentInfo.tab === 'eventseries' && paymentInfo.isPaid;
    const finalEventRegistered = eventRegistered || seriesPaid;
    
    setIsRegistered(prev => ({ ...prev, event: finalEventRegistered }));
    
    if (eventPaid) {
      setPaymentInfo({
        isPaid: true,
        amountPaid: userRegistration.amountPaid || event.price,
        receiptNumber: userRegistration.receiptNumber,
        receiptUrl: userRegistration.receiptUrl,
        tab: 'event'
      });
    } else if (!paymentInfo || paymentInfo.tab !== 'eventseries') {
      setPaymentInfo(null);
    }
    
    setShowRegistrationForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRegistrationMessage('');
    
    const hasPrice = (activeRegistrationTab === 'event' && selectedEvent.price && parseFloat(selectedEvent.price) > 0) || 
                     (activeRegistrationTab === 'eventseries' && getEventSeriesPrice() > 0);
    
    if (hasPrice && !showPaymentForm) {
      setShowPaymentForm(true);
      return;
    }
    
    if (activeRegistrationTab === 'eventseries') {
      const payload = {
        groupId: selectedEvent.groupId,
        userId: currentUser.id,
        comment: formData.comment
      };
      
      try {
        const response = await fetch(`${API_BASE_URL}/event/reservation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'session-id': currentUser?.sessionId
          },
          body: JSON.stringify(payload)
        });
        
        if (response.status === 200) {
          setRegistrationMessage(`You have successfully registered for the event series ${registrationEventGroupData?.name || 'Event Series'}`);
          setTimeout(() => {
            setShowRegistrationForm(false);
            setFormData({ firstName: '', lastName: '', email: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '', comment: '' });
            setRegistrationMessage('');
            setShowPaymentForm(false);
          }, 3000);
        } else {
          const errorText = await response.text();
          setRegistrationMessage(errorText || 'Failed to register for event series');
        }
      } catch (error) {
        setRegistrationMessage('Error registering for event series');
      }
    } else {
      const payload = {
        eventId: selectedEvent.id,
        userId: currentUser.id,
        comment: formData.comment
      };
      
      try {
        const response = await fetch(`${API_BASE_URL}/event/reservation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'session-id': currentUser?.sessionId
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          const eventDate = selectedEvent.date.toLocaleDateString();
          setRegistrationMessage(`You have successfully registered for the event ${selectedEvent.name} on ${eventDate}`);
          setTimeout(() => {
            setShowRegistrationForm(false);
            setFormData({ firstName: '', lastName: '', email: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '', comment: '' });
            setRegistrationMessage('');
            setShowPaymentForm(false);
          }, 3000);
        } else {
          const errorText = await response.text();
          setRegistrationMessage(errorText || 'Failed to register for event');
        }
      } catch (error) {
        setRegistrationMessage('Error registering for event');
      }
    }
  };

  const handleEditEvent = async (event, e) => {
    e.stopPropagation();
    
    const formatDateTimeLocal = (dateTimeStr) => {
      if (!dateTimeStr) return '';
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    const startDateTime = formatDateTimeLocal(event.startDateTime);
    const endDateTime = formatDateTimeLocal(event.endDateTime);
    
    setEventFormData({
      name: event.name || '',
      topic: event.topic || '',
      startDateTime,
      endDateTime,
      description: event.description || '',
      locationPhysical: event.locationPhysical || '',
      locationOnline: event.locationOnline || '',
      capacity: event.capacity || '',
      price: event.price || '',
      url: event.url || '',
      recordingUrl: event.recordingUrl || ''
    });
    setEditingEventId(event.id);
    setEditEventGroupData(null);
    setActiveEventTab('event');
    
    // Load event group data if event has groupId
    if (event.groupId) {
      fetchEditEventGroupData(event.groupId);
    }
    
    setShowEventForm(true);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setEventMessage('');
    
    // Validation
    if (!eventFormData.name.trim()) {
      setEventMessage('Event name is required');
      return;
    }
    if (!eventFormData.topic.trim()) {
      setEventMessage('Topic is required');
      return;
    }
    if (!eventFormData.startDateTime) {
      setEventMessage('Start date and time is required');
      return;
    }
    if (!eventFormData.endDateTime) {
      setEventMessage('End date and time is required');
      return;
    }
    if (!eventFormData.locationPhysical.trim() && !eventFormData.locationOnline.trim()) {
      setEventMessage('At least one location (Physical or Online) is required');
      return;
    }
    if (!eventFormData.capacity || parseInt(eventFormData.capacity) <= 0) {
      setEventMessage('Capacity is required and must be greater than 0');
      return;
    }
    
    // Check if end time is after start time
    const startDate = new Date(eventFormData.startDateTime);
    const endDate = new Date(eventFormData.endDateTime);
    if (endDate <= startDate) {
      setEventMessage('End time must be after start time');
      return;
    }
    
    try {
      const formatDateTime = (dateTimeLocal) => {
        const date = new Date(dateTimeLocal);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };
      
      const payload = {
        ...eventFormData,
        startDateTime: formatDateTime(eventFormData.startDateTime),
        endDateTime: formatDateTime(eventFormData.endDateTime),
        capacity: parseInt(eventFormData.capacity) || 0,
        price: parseFloat(eventFormData.price) || 0,
        url: eventFormData.url
      };
      
      if (editingEventId) {
        payload.id = editingEventId;
      }
      
      const timezoneOffset = -new Date().getTimezoneOffset();
      

      
      const response = await fetch(`${API_BASE_URL}/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId,
          'timezone-offset': timezoneOffset.toString()
        },
        body: JSON.stringify(payload)
      });
      
      if (response.status === 200) {
        const updatedEvent = await response.json();
        if (editingEventId) {
          // Update the specific event in the events array, preserving existing data
          setEvents(events.map(e => e.id === editingEventId ? { ...e, ...updatedEvent } : e));
          setEventMessage(`Event ${eventFormData.name} has been updated successfully`);
        } else {
          setEvents([...events, updatedEvent]);
          setEventMessage(`Event ${eventFormData.name} created successfully`);
        }
        
        // Refresh events using the same date range logic as useEffect
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const fromDate = new Date(firstDay);
        fromDate.setDate(firstDay.getDate() - 7);
        
        const toDate = new Date(lastDay);
        toDate.setDate(lastDay.getDate() + 7);
        
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        
        const fromDateStr = formatDate(fromDate);
        const toDateStr = formatDate(toDate);
        
        fetch(`${API_BASE_URL}/event/list/${fromDateStr}/${toDateStr}`, {
          headers: {
            'session-id': currentUser?.sessionId
          }
        })
          .then(response => response.json())
          .then(data => {
            setEvents(Array.isArray(data) ? data : []);
          })
          .catch(error => {});
        
        // Close form after 3 seconds
        setTimeout(() => {
          setShowEventForm(false);
          setEditingEventId(null);
          setActiveEventTab('event');
          setEventFormData({ name: '', topic: '', startDateTime: '', endDateTime: '', description: '', locationPhysical: '', locationOnline: '', capacity: '', price: '', url: '', recordingUrl: '' });
          setEventMessage('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setEventMessage(errorText || 'Failed to save event');
      }
    } catch (error) {
      setEventMessage('Error saving event');
    }
  };


  const isAdminOrSuperuser = currentUser && (currentUser.user?.orgRole?.toLowerCase() === 'admin' || currentUser.user?.orgRole?.toLowerCase() === 'superuser');
  const isStaffMember = currentUser && ['superuser', 'admin', 'staff'].includes(currentUser.user?.orgRole?.toLowerCase());


  const getRegistrationsForEvent = (eventId, date) => {
    const dateStr = date.toISOString().split('T')[0];
    return registrations.filter(r => r.eventId === eventId && r.eventDate === dateStr);
  };

  const handleParticipantClick = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setSelectedUser(userData);
        setShowUserDialog(true);
      }
    } catch (error) {
    }
  };

  const handleDeleteEvent = (event, day, e) => {
    e.stopPropagation();
    setEventToDelete({ event, day });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteEvent = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/event/${eventToDelete.event.id}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.status === 200) {
        setEvents(events.filter(e => e.id !== eventToDelete.event.id));
        setDeleteMessage('Event has been deleted');
        setTimeout(() => {
          setShowDeleteConfirm(false);
          setEventToDelete(null);
          setDeleteMessage('');
        }, 2000);
      } else {
        setDeleteMessage('Failed to delete event');
      }
    } catch (error) {
      setDeleteMessage('Error deleting event');
    }
  };

  const handleViewParticipants = (event, e) => {
    e.stopPropagation();
    setSelectedEventForParticipants(event);
    setShowParticipantsDialog(true);
  };

  const handleEventGroupSubmit = async (e) => {
    e.preventDefault();
    setEventGroupMessage('');
    
    try {
      const convertToMilitaryTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':');
        const h = parseInt(hours);
        const m = parseInt(minutes);
        
        // Validate time format
        if (h < 0 || h > 23 || m < 0 || m > 59) {
          return 0;
        }
        
        return h * 100 + m;
      };
      
      const payload = {
        name: eventGroupFormData.name,
        description: eventGroupFormData.description,
        daysOfWeek: eventGroupFormData.daysOfWeek.join(','),
        recurrenceType: eventGroupFormData.recurrenceType === 'weeks' ? 'weekly' : eventGroupFormData.recurrenceType,
        recurrenceInterval: eventGroupFormData.recurrenceInterval,
        occurrenceCount: eventGroupFormData.occurrenceCount,
        startDate: eventGroupFormData.startDate,
        endDate: eventGroupFormData.endDate,
        startTime: convertToMilitaryTime(eventGroupFormData.startTime),
        endTime: convertToMilitaryTime(eventGroupFormData.endTime),
        price: parseFloat(eventGroupFormData.price) || 0,
        eventPrice: parseFloat(eventGroupFormData.eventPrice) || 0,
        physicalLocation: eventGroupFormData.locationPhysical,
        onlineLocation: eventGroupFormData.locationOnline,
        capacity: parseInt(eventGroupFormData.capacity) || 0,
        url: eventGroupFormData.url,
        topic: eventGroupFormData.topic
      };
      
      const timezoneOffset = -new Date().getTimezoneOffset();
      
      const response = await fetch(`${API_BASE_URL}/event-group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId,
          'timezone-offset': timezoneOffset.toString()
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const responseData = await response.json();
        const newEvents = Array.isArray(responseData) ? responseData : [];
        const eventCount = newEvents.length || eventGroupFormData.occurrenceCount || 0;
        
        if (newEvents.length > 0) {
          setEvents(prevEvents => [...prevEvents, ...newEvents]);
        }
        
        setEventGroupMessage(`Event series created successfully with ${eventCount} events`);
        
        // Refresh events using the same date range logic as useEffect
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const fromDate = new Date(firstDay);
        fromDate.setDate(firstDay.getDate() - 7);
        
        const toDate = new Date(lastDay);
        toDate.setDate(lastDay.getDate() + 7);
        
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        
        const fromDateStr = formatDate(fromDate);
        const toDateStr = formatDate(toDate);
        
        fetch(`${API_BASE_URL}/event/list/${fromDateStr}/${toDateStr}`, {
          headers: {
            'session-id': currentUser?.sessionId
          }
        })
          .then(response => response.json())
          .then(data => {
            setEvents(Array.isArray(data) ? data : []);
          })
          .catch(error => {});
        
        setTimeout(() => {
          setShowEventGroupForm(false);
          setEventGroupFormData({
            name: '', description: '', daysOfWeek: [], recurrenceType: 'weeks',
            recurrenceInterval: 1, occurrenceCount: 10, startDate: '', endDate: '',
            startTime: '09:00', endTime: '11:00', price: '', eventPrice: '', capacity: '',
            topic: '', locationPhysical: '', locationOnline: ''
          });
          setEventGroupMessage('');
        }, 3000);
      } else {
        const responseText = await response.text();
        try {
          const errorData = JSON.parse(responseText);
          setEventGroupMessage(errorData.message || errorData.error || responseText || 'Failed to create event group');
        } catch {
          setEventGroupMessage(responseText || 'Failed to create event group');
        }
      }
    } catch (error) {
      setEventGroupMessage('Error creating event group');
    }
  };

  const getWeekDates = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      dates.push(day);
    }
    return dates;
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour < 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = new Date();
        time.setHours(hour, minute, 0, 0);
        slots.push(time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }));
      }
    }
    return slots;
  };

  const getEventsForDateTime = (date, timeSlot) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      if (!event.startDateTime) return false;
      const eventDate = new Date(event.startDateTime);
      const eventDateStr = eventDate.toISOString().split('T')[0];
      const eventTime = formatTime(event.startDateTime);
      return eventDateStr === dateStr && eventTime === timeSlot;
    });
  };

  const getEventDurationInSlots = (event) => {
    if (!event.startDateTime || !event.endDateTime) return 1;
    const start = new Date(event.startDateTime);
    const end = new Date(event.endDateTime);
    const durationMs = end - start;
    const durationMinutes = durationMs / (1000 * 60);
    return Math.max(1, Math.ceil(durationMinutes / 30));
  };

  const isEventStartSlot = (event, date, timeSlot) => {
    const dateStr = date.toISOString().split('T')[0];
    const eventDate = new Date(event.startDateTime);
    const eventDateStr = eventDate.toISOString().split('T')[0];
    const eventTime = formatTime(event.startDateTime);
    return eventDateStr === dateStr && eventTime === timeSlot;
  };

  const handleTimeSlotClick = (selectedDate, timeSlot) => {
    if (!isAdminOrSuperuser) return;
    
    const [time, period] = timeSlot.split(' ');
    const [hours, minutes] = time.split(':');
    let hour24 = parseInt(hours);
    if (period === 'PM' && hour24 !== 12) hour24 += 12;
    if (period === 'AM' && hour24 === 12) hour24 = 0;
    
    const startDate = new Date(selectedDate);
    startDate.setHours(hour24, parseInt(minutes), 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setHours(startDate.getHours() + 2);
    
    const formatDateTimeLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    setEventFormData({
      name: '',
      topic: '',
      startDateTime: formatDateTimeLocal(startDate),
      endDateTime: formatDateTimeLocal(endDate),
      description: '',
      locationPhysical: '',
      locationOnline: '',
      capacity: '',
      price: ''
    });
    setEditingEventId(null);
    setActiveEventTab('event');
    setShowEventForm(true);
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(date);
    const timeSlots = getTimeSlots();
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '100px repeat(7, 1fr)', gap: '1px', backgroundColor: '#ccc' }}>
          <div style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', textAlign: 'center', fontWeight: 'bold' }}>Time</div>
          {weekDates.map(day => (
            <div key={day.toISOString()} style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', textAlign: 'center', fontWeight: 'bold' }}>
              {day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px repeat(7, 1fr)', gap: '1px', backgroundColor: '#ccc' }}>
            {timeSlots.map(timeSlot => (
              <React.Fragment key={timeSlot}>
                <div style={{ backgroundColor: '#f8f9fa', padding: '0.25rem', textAlign: 'center', fontSize: '0.8rem', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {timeSlot}
                </div>
                {weekDates.map(day => {
                  const dayEvents = getEventsForDateTime(day, timeSlot);
                  return (
                    <div key={`${day.toISOString()}-${timeSlot}`} 
                         style={{ backgroundColor: 'white', padding: '2px', minHeight: '40px', position: 'relative', cursor: isAdminOrSuperuser ? 'pointer' : 'default' }}
                         onClick={() => dayEvents.length === 0 && handleTimeSlotClick(day, timeSlot)}>
                      {dayEvents.filter(event => isEventStartSlot(event, day, timeSlot)).map(event => {
                        const duration = getEventDurationInSlots(event);
                        return (
                          <div key={event.id} 
                               style={{ 
                                 fontSize: '0.7rem', 
                                 backgroundColor: '#e8f5e8', 
                                 padding: '2px', 
                                 margin: '1px', 
                                 borderRadius: '2px',
                                 cursor: 'pointer',
                                 border: '1px solid #28a745',
                                 position: 'absolute',
                                 top: '2px',
                                 left: '2px',
                                 right: '2px',
                                 height: `${(duration * 40) + ((duration - 1) * 1) - 4}px`,
                                 zIndex: 10
                               }}
                               onClick={() => handleEventClick(event, day)}>
                          <div style={{ position: 'absolute', top: '1px', right: '1px', display: 'flex', gap: '2px' }}>
                            {isStaffMember && (
                              <div style={{ cursor: 'pointer', fontSize: '0.6rem' }}
                                   onClick={(e) => handleViewParticipants(event, e)}>
                                👥
                              </div>
                            )}
                            {isAdminOrSuperuser && (
                              <>
                                <div style={{ cursor: 'pointer', fontSize: '0.5rem' }}
                                     onClick={(e) => handleDeleteEvent(event, day, e)}>
                                  🗑️
                                </div>
                                <div style={{ cursor: 'pointer', fontSize: '0.5rem' }}
                                     onClick={(e) => handleEditEvent(event, e)}>
                                  ✏️
                                </div>
                              </>
                            )}
                          </div>
                            <div style={{ fontWeight: 'bold', fontSize: '0.6rem' }}>{event.name}</div>
                            <div style={{ fontSize: '0.5rem' }}>{event.topic}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const timeSlots = getTimeSlots();
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1px', backgroundColor: '#ccc' }}>
          <div style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', textAlign: 'center', fontWeight: 'bold' }}>Time</div>
          <div style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', textAlign: 'center', fontWeight: 'bold' }}>
            {date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1px', backgroundColor: '#ccc' }}>
            {timeSlots.map(timeSlot => {
              const dayEvents = getEventsForDateTime(date, timeSlot);
              return (
                <React.Fragment key={timeSlot}>
                  <div style={{ backgroundColor: '#f8f9fa', padding: '0.25rem', textAlign: 'center', fontSize: '0.8rem', minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {timeSlot}
                  </div>
                  <div style={{ backgroundColor: 'white', padding: '2px', minHeight: '40px', position: 'relative', cursor: isAdminOrSuperuser ? 'pointer' : 'default' }}
                       onClick={() => dayEvents.length === 0 && handleTimeSlotClick(date, timeSlot)}>
                    {dayEvents.filter(event => isEventStartSlot(event, date, timeSlot)).map(event => {
                      const duration = getEventDurationInSlots(event);
                      return (
                        <div key={event.id} 
                             style={{ 
                               fontSize: '0.8rem', 
                               backgroundColor: '#e8f5e8', 
                               padding: '4px', 
                               margin: '2px', 
                               borderRadius: '4px',
                               cursor: 'pointer',
                               border: '1px solid #28a745',
                               position: 'absolute',
                               top: '2px',
                               left: '2px',
                               right: '2px',
                               height: `${(duration * 40) + ((duration - 1) * 1) - 4}px`,
                               zIndex: 10
                             }}
                             onClick={() => handleEventClick(event, date)}>
                        <div style={{ position: 'absolute', top: '2px', right: '2px', display: 'flex', gap: '3px' }}>
                          {isStaffMember && (
                            <div style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                                 onClick={(e) => handleViewParticipants(event, e)}>
                              👥
                            </div>
                          )}
                          {isAdminOrSuperuser && (
                            <>
                              <div style={{ cursor: 'pointer', fontSize: '0.6rem' }}
                                   onClick={(e) => handleDeleteEvent(event, date, e)}>
                                🗑️
                              </div>
                              <div style={{ cursor: 'pointer', fontSize: '0.6rem' }}
                                   onClick={(e) => handleEditEvent(event, e)}>
                                ✏️
                              </div>
                            </>
                          )}
                        </div>
                        <div style={{ fontWeight: 'bold' }}>{event.name}</div>
                        <div>{event.topic}</div>
                        <div>{formatTime(event.startDateTime)} - {formatTime(event.endDateTime)}</div>
                        {event.price && <div style={{ fontSize: '0.6rem', color: '#007bff', fontWeight: 'bold' }}>${event.price}</div>}
                          <div style={{ fontSize: '0.6rem', color: '#666' }}>
                            {(currentUser?.user?.orgRole === 'staff' || currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') 
                              ? `${event.participantList?.length || 0} registered` 
                              : event.slotsRemaining !== undefined ? `${event.slotsRemaining} slots remaining` : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates(date);
    const currentMonth = date.getMonth();
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', backgroundColor: '#ccc', height: 'calc(100vh - 200px)' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', textAlign: 'center', fontWeight: 'bold' }}>
            {day}
          </div>
        ))}
        
        {monthDates.map(day => {
          const dayEvents = getEventsForDate(day);
          const isCurrentMonth = day.getMonth() === currentMonth;
          
          return (
            <div key={day.toISOString()} 
                 style={{ 
                   backgroundColor: 'white', 
                   padding: '0.25rem', 
                   minHeight: '80px',
                   maxHeight: '120px',
                   opacity: isCurrentMonth ? 1 : 0.3,
                   overflow: 'auto',
                   display: 'flex',
                   flexDirection: 'column'
                 }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <span>{day.getDate()}</span>
                {isAdminOrSuperuser && (
                  <span 
                    style={{ 
                      fontSize: '16px', 
                      color: '#007bff', 
                      cursor: 'pointer', 
                      padding: '2px 4px',
                      borderRadius: '3px',
                      lineHeight: '1'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const startDate = new Date(day);
                      startDate.setHours(9, 0, 0, 0);
                      const endDate = new Date(startDate);
                      endDate.setHours(11, 0, 0, 0);
                      const formatDateTimeLocal = (date) => {
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day}T${hours}:${minutes}`;
                      };
                      setEventFormData({
                        name: '',
                        topic: '',
                        startDateTime: formatDateTimeLocal(startDate),
                        endDateTime: formatDateTimeLocal(endDate),
                        description: '',
                        locationPhysical: '',
                        locationOnline: '',
                        capacity: '',
                        price: '',
                        url: ''
                      });
                      setEditingEventId(null);
                      setActiveEventTab('event');
                      setShowEventForm(true);
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    title="Add new event"
                  >
                    +
                  </span>
                )}
              </div>
              {dayEvents.map(event => {
                const eventRegistrations = getRegistrationsForEvent(event.id, day);
                return (
                  <div key={event.id} 
                       style={{ 
                         fontSize: '0.7rem', 
                         backgroundColor: '#e8f5e8', 
                         padding: '2px', 
                         margin: '1px', 
                         borderRadius: '2px',
                         cursor: 'pointer',
                         border: '1px solid #28a745',
                         position: 'relative'
                       }}
                       onClick={() => handleEventClick(event, day)}>
                    <div style={{ position: 'absolute', top: '2px', right: '2px', display: 'flex', gap: '3px' }}>
                      {isStaffMember && (
                        <div style={{ cursor: 'pointer', fontSize: '1rem' }}
                             onClick={(e) => handleViewParticipants(event, e)}>
                          👥
                        </div>
                      )}
                      {isAdminOrSuperuser && (
                        <>
                          <div style={{ cursor: 'pointer', fontSize: '0.6rem' }}
                               onClick={(e) => handleDeleteEvent(event, day, e)}>
                            🗑️
                          </div>
                          <div style={{ cursor: 'pointer', fontSize: '0.6rem' }}
                               onClick={(e) => handleEditEvent(event, e)}>
                            ✏️
                          </div>
                        </>
                      )}
                    </div>
                    <div style={{ fontWeight: 'bold' }}>{event.name}</div>
                    <div>{event.topic}</div>
                    <div>{formatTime(event.startDateTime)} - {formatTime(event.endDateTime)}</div>
                    {event.price && <div style={{ fontSize: '0.6rem', color: '#007bff', fontWeight: 'bold' }}>${event.price}</div>}
                    <div style={{ fontSize: '0.6rem', color: '#666' }}>
                      {(currentUser?.user?.orgRole === 'staff' || currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') 
                        ? `${event.participantList?.length || 0} registered` 
                        : event.slotsRemaining !== undefined ? `${event.slotsRemaining} slots remaining` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <WelcomeMessage />
      <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>
            Events - {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          {isAdminOrSuperuser && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => setShowEventForm(true)} style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Add Event</button>
              <button onClick={() => setShowEventGroupForm(true)} style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Add Event Series</button>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button onClick={() => {
            const newDate = new Date(date);
            if (view === 'month') {
              newDate.setMonth(date.getMonth() - 1);
            } else if (view === 'week') {
              newDate.setDate(date.getDate() - 7);
            } else {
              newDate.setDate(date.getDate() - 1);
            }
            setDate(newDate);
          }}>← Previous</button>
          
          <button onClick={() => setDate(new Date())}>Today</button>
          
          <button onClick={() => {
            const newDate = new Date(date);
            if (view === 'month') {
              newDate.setMonth(date.getMonth() + 1);
            } else if (view === 'week') {
              newDate.setDate(date.getDate() + 7);
            } else {
              newDate.setDate(date.getDate() + 1);
            }
            setDate(newDate);
          }}>Next →</button>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setView('day')} style={{ backgroundColor: view === 'day' ? '#007bff' : '#fff', color: view === 'day' ? '#fff' : '#000', border: '1px solid #007bff', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}>Day</button>
            <button onClick={() => setView('week')} style={{ backgroundColor: view === 'week' ? '#007bff' : '#fff', color: view === 'week' ? '#fff' : '#000', border: '1px solid #007bff', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}>Week</button>
            <button onClick={() => setView('month')} style={{ backgroundColor: view === 'month' ? '#007bff' : '#fff', color: view === 'month' ? '#fff' : '#000', border: '1px solid #007bff', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}>Month</button>
          </div>
        </div>
      </div>
      
      <div style={{ flex: 1, overflow: 'auto' }}>
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'day' && renderDayView()}
      </div>

      {showRegistrationForm && selectedEvent && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                     backgroundColor: '#f0f8ff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: '480px', height: '750px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0', padding: '1rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc' }}>
            <h3 style={{ margin: '0', flex: 1, textAlign: 'center' }}>Register for Event</h3>
            <button onClick={() => { 
              setShowRegistrationForm(false); 
              setRegistrationMessage(''); 
              setRegistrationEventGroupData(null); 
              setActiveRegistrationTab('event'); 
              setShowPaymentForm(false); 
              setCardInstance(null); 
              setPaymentInfo(null); 
              setFormData({ firstName: '', lastName: '', email: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '', comment: '' });
              // Don't reset isRegistered - user remains registered
              setPaymentFormInitialized(false);
            }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {selectedEvent?.groupId && (
            <div style={{ display: 'flex', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => {
                setActiveRegistrationTab('event');
                setShowPaymentForm(false);
                setCardInstance(null);
                setPaymentFormInitialized(false);
                // Preserve registration status when switching tabs
                const userRegistration = selectedEvent.participantList?.find(p => p.userId === currentUser?.user?.id);
                const eventRegistered = !!userRegistration;
                const seriesPaid = paymentInfo && paymentInfo.tab === 'eventseries' && paymentInfo.isPaid;
                const finalEventRegistered = eventRegistered || seriesPaid;
                setIsRegistered(prev => ({ ...prev, event: finalEventRegistered }));
              }} style={{ 
                padding: '0.75rem 1.5rem', 
                backgroundColor: '#e6f3ff', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: activeRegistrationTab === 'event' ? '1px solid #e6f3ff' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: activeRegistrationTab === 'event' ? 'bold' : 'normal',
                marginRight: '2px'
              }}>Register For Event</button>
              <button type="button" onClick={() => {
                setActiveRegistrationTab('eventseries');
                setShowPaymentForm(false);
                setCardInstance(null);
                setPaymentFormInitialized(false);
                if (selectedEvent?.groupId) {
                  if (!registrationEventGroupData) {
                    fetchRegistrationEventGroupData(selectedEvent.groupId);
                  }
                }
                // Preserve registration status when switching tabs - will be updated by fetchRegistrationEventGroupData
              }} style={{ 
                padding: '0.75rem 1.5rem', 
                backgroundColor: '#fff2e6', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: activeRegistrationTab === 'eventseries' ? '1px solid #fff2e6' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: activeRegistrationTab === 'eventseries' ? 'bold' : 'normal'
              }}>Register For Event Series</button>
            </div>
          )}
          <div style={{ flex: 1, overflow: 'auto', backgroundColor: activeRegistrationTab === 'eventseries' ? '#fff2e6' : '#e6f3ff', border: selectedEvent?.groupId ? '1px solid #ccc' : 'none', borderTop: selectedEvent?.groupId ? 'none' : 'none' }}>
            <form id="registration-form" onSubmit={handleSubmit} style={{ padding: '2rem' }}>
            {activeRegistrationTab === 'event' && (
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem', 
                backgroundColor: '#ffffff', 
                border: '1px solid #d1ecf1', 
                borderRadius: '8px',
                textAlign: 'left'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '1rem', color: '#333' }}>{selectedEvent.name}</div>
                <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>{selectedEvent.topic}</div>
                {selectedEvent.description && <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>{selectedEvent.description}</div>}
                <div style={{ fontSize: '0.9rem', color: '#666' }}>{selectedEvent.date.toLocaleDateString()} at {formatTime(selectedEvent.startDateTime)} - {formatTime(selectedEvent.endDateTime)}</div>
                {selectedEvent.locationPhysical && <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>Physical Location: {selectedEvent.locationPhysical}</div>}
                {selectedEvent.locationOnline && <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>Online Location: {selectedEvent.locationOnline}</div>}
                {selectedEvent.url && <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>URL: <a href={selectedEvent.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>{selectedEvent.url}</a></div>}
                {selectedEvent.recordingUrl && selectedEvent.recordingUrl.trim() !== '' && isRegistered.event && <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>Recording: <a href={selectedEvent.recordingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>View Recording</a></div>}
                {selectedEvent.price && <div style={{ fontSize: '0.9rem', color: '#007bff', fontWeight: 'bold', marginTop: '0.5rem' }}>Price: ${selectedEvent.price}</div>}
                {(() => {
                  const userParticipant = selectedEvent.participantList?.find(p => p.userId === currentUser?.user?.id);
                  const isEventPaid = userParticipant && userParticipant.isPaid && userParticipant.amountPaid;
                  const isSeriesPaid = paymentInfo && paymentInfo.tab === 'eventseries' && paymentInfo.isPaid;
                  const isEventPaidFromPaymentInfo = paymentInfo && paymentInfo.tab === 'event' && paymentInfo.isPaid;
                  
                  if (isEventPaid || isEventPaidFromPaymentInfo) {
                    const amountPaid = isEventPaidFromPaymentInfo ? paymentInfo.amountPaid : userParticipant.amountPaid;
                    const receiptUrl = isEventPaidFromPaymentInfo ? paymentInfo.receiptUrl : userParticipant.receiptUrl;
                    return (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
                        <div style={{ fontSize: '0.9rem', color: '#155724', fontWeight: 'bold' }}>✓ Paid: ${parseFloat(amountPaid).toFixed(2)}</div>
                        {receiptUrl && (
                          <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            <a href={receiptUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>View Receipt</a>
                          </div>
                        )}
                      </div>
                    );
                  } else if (isSeriesPaid && selectedEvent.groupId) {
                    return (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
                        <div style={{ fontSize: '0.9rem', color: '#155724', fontWeight: 'bold' }}>✓ Paid (Series)</div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            {activeRegistrationTab === 'eventseries' && (
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem', 
                backgroundColor: '#ffffff', 
                border: '1px solid #d1ecf1', 
                borderRadius: '8px',
                textAlign: 'left'
              }}>
                {registrationEventGroupData ? (
                  registrationEventGroupData.error ? (
                    <div style={{ textAlign: 'center', color: '#dc3545', padding: '1rem' }}>{registrationEventGroupData.error}</div>
                  ) : (
                    <>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '1rem', color: '#333' }}>{registrationEventGroupData.name}</div>
                      <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>{registrationEventGroupData.topic}</div>
                      <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>{registrationEventGroupData.description}</div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>{new Date(registrationEventGroupData.startDate).toLocaleDateString('en-US')} to {new Date(registrationEventGroupData.endDate).toLocaleDateString('en-US')}</div>
                      {registrationEventGroupData.locationPhysical && <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>Physical Location: {registrationEventGroupData.locationPhysical}</div>}
                      {registrationEventGroupData.locationOnline && <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>Online Location: {registrationEventGroupData.locationOnline}</div>}
                      {registrationEventGroupData.url && <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>URL: <a href={registrationEventGroupData.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>{registrationEventGroupData.url}</a></div>}
                      <div style={{ fontSize: '0.9rem', color: '#007bff', fontWeight: 'bold', marginTop: '0.5rem' }}>
                        Series Price: 
                        {registrationEventGroupData.discountPercent && registrationEventGroupData.discountPercent > 0 ? (
                          <>
                            <span style={{ textDecoration: 'line-through', marginRight: '0.5rem' }}>${registrationEventGroupData.price || registrationEventGroupData.groupPrice || '0.00'}</span>
                            <span>${(Math.round(parseFloat(registrationEventGroupData.price || registrationEventGroupData.groupPrice || 0) * (1 - registrationEventGroupData.discountPercent / 100) * 100) / 100).toFixed(2)}</span>
                          </>
                        ) : (
                          `$${registrationEventGroupData.price || registrationEventGroupData.groupPrice || '0.00'}`
                        )}
                      </div>
                      {(() => {
                        const userReservation = registrationEventGroupData.reservation && registrationEventGroupData.reservation.userId === currentUser?.user?.id ? registrationEventGroupData.reservation : null;
                        const isSeriesPaid = userReservation && userReservation.isPaid;
                        
                        if (isSeriesPaid) {
                          return (
                            <div style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px' }}>
                              <div style={{ fontSize: '0.9rem', color: '#155724', fontWeight: 'bold' }}>✓ Paid: ${userReservation.amountPaid || registrationEventGroupData.price || '0.00'}</div>
                              {userReservation.receiptUrl && (
                                <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                                  <a href={userReservation.receiptUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>View Receipt</a>
                                </div>
                              )}
                              {userReservation.receiptNumber && (
                                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>Receipt #: {userReservation.receiptNumber}</div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </>
                  )
                ) : (
                  <div style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>Loading event series information...</div>
                )}
              </div>
            )}
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>First Name:</label>
              <input type="text" value={formData.firstName} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Last Name:</label>
              <input type="text" value={formData.lastName} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Email:</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Phone:</label>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
              <label style={{ width: '30%', paddingTop: '0.25rem' }}>Comment:</label>
              <textarea value={formData.comment} onChange={(e) => setFormData({...formData, comment: e.target.value})} rows="3" style={{ width: '70%', resize: 'vertical' }} />
            </div>
            {showPaymentForm && ((activeRegistrationTab === 'event' && selectedEvent.price && parseFloat(selectedEvent.price) > 0) || (activeRegistrationTab === 'eventseries' && getEventSeriesPrice() > 0)) && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Payment Information:</label>
                <div id="card-container" style={{ minHeight: '75px', border: '1px solid #ccc', borderRadius: '4px', padding: '1rem', position: 'relative' }}>
                  {!paymentFormInitialized && (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#666' }}>
                      {squareLoaded ? 'Initializing payment form...' : 'Loading payment system...'}
                    </div>
                  )}
                </div>
              </div>
            )}
            </form>
          </div>
          <div style={{ padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa' }}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              {!showPaymentForm ? (
                <button 
                  type={isRegistered[activeRegistrationTab] ? "button" : "submit"} 
                  form={isRegistered[activeRegistrationTab] ? undefined : "registration-form"}
                  disabled={isRegistered[activeRegistrationTab] || (activeRegistrationTab === 'eventseries' && !registrationEventGroupData)}
                  style={{ 
                    backgroundColor: isRegistered[activeRegistrationTab] ? '#28a745' : '#007bff', 
                    color: 'white',
                    opacity: (isRegistered[activeRegistrationTab] || (activeRegistrationTab === 'eventseries' && !registrationEventGroupData)) ? 0.8 : 1,
                    cursor: (isRegistered[activeRegistrationTab] || (activeRegistrationTab === 'eventseries' && !registrationEventGroupData)) ? 'default' : 'pointer'
                  }}
                >
                  {isRegistered[activeRegistrationTab] ? 'Registered' : (activeRegistrationTab === 'eventseries' ? 'Register for Series' : 'Register')}
                </button>
              ) : (
                <button type="button" onClick={async () => {
                  try {
                    if (!cardInstance) {
                      setRegistrationMessage('Error: Payment form not initialized');
                      return;
                    }
                    
                    const tokenResult = await cardInstance.tokenize();
                    
                    if (tokenResult.status === 'OK') {
                      const nonce = tokenResult.token;
                      
                      const response = await fetch(`${API_BASE_URL}/process-payment`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'session-id': currentUser?.sessionId
                        },
                        body: JSON.stringify({
                          nonce,
                          amount: activeRegistrationTab === 'eventseries' ? getEventSeriesPrice() * 100 : parseFloat(selectedEvent.price) * 100,
                          eventId: activeRegistrationTab === 'eventseries' ? null : selectedEvent.id,
                          groupId: activeRegistrationTab === 'eventseries' ? registrationEventGroupData.id : null,
                          userId: currentUser.id,
                          description: activeRegistrationTab === 'eventseries' ? `Payment for ${registrationEventGroupData.name}` : `Payment for ${selectedEvent.name}`
                        })
                      });
                      
                      if (response.ok) {
                        const paymentData = await response.json();
                        setPaymentInfo({
                          isPaid: true,
                          amountPaid: paymentData.amountPaid || (activeRegistrationTab === 'eventseries' ? getEventSeriesPrice() : selectedEvent.price),
                          receiptNumber: paymentData.receiptNumber,
                          receiptUrl: paymentData.receiptUrl,
                          tab: activeRegistrationTab
                        });
                        
                        // Update event participant list to show paid status immediately
                        if (activeRegistrationTab === 'event') {
                          const updatedParticipant = {
                            userId: currentUser?.user?.id,
                            firstName: formData.firstName,
                            lastName: formData.lastName,
                            email: formData.email,
                            phone: formData.phone,
                            comment: formData.comment,
                            isPaid: true,
                            amountPaid: paymentData.amountPaid || selectedEvent.price,
                            receiptUrl: paymentData.receiptUrl,
                            receiptNumber: paymentData.receiptNumber
                          };
                          
                          setSelectedEvent(prev => {
                            const existingParticipant = prev.participantList?.find(p => p.userId === currentUser?.user?.id);
                            if (existingParticipant) {
                              return {
                                ...prev,
                                participantList: prev.participantList.map(p => 
                                  p.userId === currentUser?.user?.id ? { ...p, ...updatedParticipant } : p
                                )
                              };
                            } else {
                              return {
                                ...prev,
                                participantList: [...(prev.participantList || []), updatedParticipant]
                              };
                            }
                          });
                          
                          // Also update the main events array
                          setEvents(prevEvents => 
                            prevEvents.map(event => {
                              if (event.id === selectedEvent.id) {
                                const existingParticipant = event.participantList?.find(p => p.userId === currentUser?.user?.id);
                                if (existingParticipant) {
                                  return {
                                    ...event,
                                    participantList: event.participantList.map(p => 
                                      p.userId === currentUser?.user?.id ? { ...p, ...updatedParticipant } : p
                                    )
                                  };
                                } else {
                                  return {
                                    ...event,
                                    participantList: [...(event.participantList || []), updatedParticipant]
                                  };
                                }
                              }
                              return event;
                            })
                          );
                        } else if (activeRegistrationTab === 'eventseries') {
                          // Update event series registration status
                          setRegistrationEventGroupData(prev => ({
                            ...prev,
                            reservation: {
                              ...prev.reservation,
                              userId: currentUser?.user?.id,
                              isPaid: true,
                              amountPaid: paymentData.amountPaid || getEventSeriesPrice(),
                              receiptNumber: paymentData.receiptNumber,
                              receiptUrl: paymentData.receiptUrl
                            }
                          }));
                        }
                        
                        setRegistrationMessage('Payment successful!');
                        setShowPaymentForm(false);
                        setCardInstance(null);
                        setPaymentFormInitialized(false);
                        
                        // Update registration status for both tabs
                        if (activeRegistrationTab === 'eventseries') {
                          setIsRegistered({ event: true, eventseries: true });
                        } else {
                          setIsRegistered(prev => ({ ...prev, [activeRegistrationTab]: true }));
                        }
                      } else {
                        const errorText = await response.text();
                        setRegistrationMessage(`Error: Payment failed - ${errorText}`);
                      }
                    } else {
                      const missingFields = [];
                      if (tokenResult.errors) {
                        tokenResult.errors.forEach(error => {
                          if (error.message.includes('Credit card number')) missingFields.push('card number');
                          if (error.message.includes('CVV')) missingFields.push('CVV');
                          if (error.message.includes('Expiration date')) missingFields.push('expiration date');
                        });
                      }
                      const message = missingFields.length > 0 
                        ? `Please fill in the following fields: ${missingFields.join(', ')}`
                        : 'Please fill in all credit card fields';
                      setRegistrationMessage(message);
                    }
                  } catch (error) {
                    setRegistrationMessage(`Error: ${error.message || 'Payment processing failed'}`);
                  }
                }} disabled={!squareLoaded || !paymentFormInitialized} style={{ backgroundColor: '#28a745', color: 'white', opacity: (squareLoaded && paymentFormInitialized) ? 1 : 0.5, cursor: (squareLoaded && paymentFormInitialized) ? 'pointer' : 'not-allowed' }}>Pay For {activeRegistrationTab === 'eventseries' ? 'Series' : 'Event'} (${activeRegistrationTab === 'eventseries' ? getEventSeriesPrice().toFixed(2) : selectedEvent.price})</button>
              )}
              <button type="button" onClick={() => { 
                setShowRegistrationForm(false); 
                setRegistrationMessage(''); 
                setRegistrationEventGroupData(null); 
                setActiveRegistrationTab('event'); 
                setShowPaymentForm(false); 
                setCardInstance(null); 
                setPaymentInfo(null); 
                setFormData({ firstName: '', lastName: '', email: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '', comment: '' });
                // Don't reset isRegistered - user remains registered
                setPaymentFormInitialized(false);
              }} style={{ marginLeft: '1rem' }}>{isRegistered[activeRegistrationTab] ? 'Close' : 'Cancel'}</button>
            </div>
            {registrationMessage && (
              <div style={{ padding: '0.75rem', borderRadius: '4px', backgroundColor: registrationMessage.includes('Error') || registrationMessage.includes('Failed') ? '#f8d7da' : '#d4edda', color: registrationMessage.includes('Error') || registrationMessage.includes('Failed') ? '#721c24' : '#155724', border: `1px solid ${registrationMessage.includes('Error') || registrationMessage.includes('Failed') ? '#f5c6cb' : '#c3e6cb'}`, textAlign: 'center' }}>
                {registrationMessage}
              </div>
            )}
          </div>
        </div>
      )}

      {showEventForm && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                     backgroundColor: '#f0f8ff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: '600px', height: '600px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0', padding: '1rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc' }}>
            <h3 style={{ margin: '0', flex: 1, textAlign: 'center' }}>{editingEventId ? 'Edit Event' : 'Create New Event'}</h3>
            <button onClick={() => setShowEventForm(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          {eventMessage && (
            <div style={{ padding: '0.75rem', margin: '1rem', borderRadius: '4px', backgroundColor: eventMessage.includes('Error') || eventMessage.includes('Failed') ? '#f8d7da' : '#d4edda', color: eventMessage.includes('Error') || eventMessage.includes('Failed') ? '#721c24' : '#155724', border: `1px solid ${eventMessage.includes('Error') || eventMessage.includes('Failed') ? '#f5c6cb' : '#c3e6cb'}` }}>
              {eventMessage}
            </div>
          )}
          <div style={{ display: 'flex', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
            <button type="button" onClick={() => setActiveEventTab('event')} style={{ 
              padding: '0.75rem 1.5rem', 
              backgroundColor: activeEventTab === 'event' ? '#e6f3ff' : '#e9ecef', 
              color: '#495057',
              border: '1px solid #ccc', 
              borderBottom: activeEventTab === 'event' ? '1px solid #e6f3ff' : '1px solid #ccc',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              cursor: 'pointer',
              fontWeight: activeEventTab === 'event' ? 'bold' : 'normal',
              marginRight: '2px'
            }}>Event</button>
            {(() => {
              const currentEvent = events.find(e => e.id === editingEventId);
              return currentEvent?.groupId && (
                <button type="button" onClick={() => {
                  setActiveEventTab('eventgroup');
                  if (currentEvent?.groupId && !editEventGroupData) {
                    fetchEditEventGroupData(currentEvent.groupId);
                  }
                }} style={{ 
                padding: '0.75rem 1.5rem', 
                backgroundColor: activeEventTab === 'eventgroup' ? '#fff2e6' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: activeEventTab === 'eventgroup' ? '1px solid #fff2e6' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: activeEventTab === 'eventgroup' ? 'bold' : 'normal',
                marginRight: '2px'
              }}>Event Series</button>
              );
            })()}
            <button type="button" onClick={() => setActiveEventTab('participants')} style={{ 
              padding: '0.75rem 1.5rem', 
              backgroundColor: activeEventTab === 'participants' ? '#f0f8f0' : '#e9ecef', 
              color: '#495057',
              border: '1px solid #ccc', 
              borderBottom: activeEventTab === 'participants' ? '1px solid #f0f8f0' : '1px solid #ccc',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              cursor: 'pointer',
              fontWeight: activeEventTab === 'participants' ? 'bold' : 'normal'
            }}>Participants</button>
          </div>
          <form onSubmit={handleEventSubmit} style={{ padding: '2rem', flex: 1, overflow: 'auto', backgroundColor: activeEventTab === 'event' ? '#e6f3ff' : activeEventTab === 'eventgroup' ? '#fff2e6' : '#f0f8f0', border: '1px solid #ccc', borderTop: 'none' }}>
            {activeEventTab === 'event' && (
              <div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Name:</label>
                  <input type="text" value={eventFormData.name} onChange={(e) => setEventFormData({...eventFormData, name: e.target.value})} required />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Topic:</label>
                  <input type="text" value={eventFormData.topic} onChange={(e) => setEventFormData({...eventFormData, topic: e.target.value})} required />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Start Date & Time:</label>
                  <input type="datetime-local" value={eventFormData.startDateTime} onChange={(e) => setEventFormData({...eventFormData, startDateTime: e.target.value})} required />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>End Date & Time:</label>
                  <input type="datetime-local" value={eventFormData.endDateTime} onChange={(e) => setEventFormData({...eventFormData, endDateTime: e.target.value})} required />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
                  <label style={{ width: '50%', paddingTop: '0.25rem' }}>Description:</label>
                  <textarea value={eventFormData.description} onChange={(e) => setEventFormData({...eventFormData, description: e.target.value})} rows="3" style={{ width: '50%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Physical Location:</label>
                  <input type="text" value={eventFormData.locationPhysical} onChange={(e) => setEventFormData({...eventFormData, locationPhysical: e.target.value})} />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Online Location:</label>
                  <input type="text" value={eventFormData.locationOnline} onChange={(e) => setEventFormData({...eventFormData, locationOnline: e.target.value})} />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Capacity:</label>
                  <input type="number" value={eventFormData.capacity} onChange={(e) => setEventFormData({...eventFormData, capacity: e.target.value})} min="1" />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Price:</label>
                  <input type="number" value={eventFormData.price} onChange={(e) => setEventFormData({...eventFormData, price: e.target.value})} min="0" step="0.01" placeholder="0.00" />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>URL:</label>
                  <input type="url" value={eventFormData.url} onChange={(e) => setEventFormData({...eventFormData, url: e.target.value})} placeholder="https://" />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Recording URL:</label>
                  <input type="url" value={eventFormData.recordingUrl || ''} onChange={(e) => setEventFormData({...eventFormData, recordingUrl: e.target.value})} placeholder="https://..." />
                </div>
              </div>
            )}
            {activeEventTab === 'participants' && editingEventId && (
              <div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                      <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>First Name</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Last Name</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Email</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Phone</th>
                      <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const currentEvent = events.find(e => e.id === editingEventId);
                      const participants = currentEvent?.participantList || [];
                      return participants.length > 0 ? participants.map((participant, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer', color: '#007bff' }} onClick={() => handleParticipantClick(participant.userId)}>{participant.firstName || ''}</td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer', color: '#007bff' }} onClick={() => handleParticipantClick(participant.userId)}>{participant.lastName || ''}</td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{participant.email || ''}</td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', whiteSpace: 'nowrap' }}>{participant.phone || ''}</td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{participant.comment || ''}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="5" style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>No participants registered</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
            {activeEventTab === 'eventgroup' && editEventGroupData && (
              <div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Name:</label>
                  <input type="text" value={editEventGroupData.name || ''} onChange={(e) => setEditEventGroupData({...editEventGroupData, name: e.target.value})} />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
                  <label style={{ width: '30%', paddingTop: '0.25rem' }}>Description:</label>
                  <textarea value={editEventGroupData.description || ''} onChange={(e) => setEditEventGroupData({...editEventGroupData, description: e.target.value})} rows="2" style={{ width: '70%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Physical Location:</label>
                  <input type="text" value={editEventGroupData.locationPhysical || ''} onChange={(e) => setEditEventGroupData({...editEventGroupData, locationPhysical: e.target.value})} />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Online Location:</label>
                  <input type="text" value={editEventGroupData.locationOnline || ''} onChange={(e) => setEditEventGroupData({...editEventGroupData, locationOnline: e.target.value})} />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Individual Event Price:</label>
                  <input type="number" value={editEventGroupData.eventPrice || ''} onChange={(e) => setEditEventGroupData({...editEventGroupData, eventPrice: e.target.value})} min="0" step="0.01" placeholder="0.00" />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Series Price:</label>
                  <input type="number" value={editEventGroupData.price || ''} onChange={(e) => setEditEventGroupData({...editEventGroupData, price: e.target.value})} min="0" step="0.01" placeholder="0.00" />
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>URL:</label>
                  <input type="url" value={editEventGroupData.url || ''} onChange={(e) => setEditEventGroupData({...editEventGroupData, url: e.target.value})} placeholder="https://" />
                </div>
                <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #ccc' }}>
                  <button type="button" onClick={async () => {
                    try {
                      const response = await fetch(`${API_BASE_URL}/event-group`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'session-id': currentUser?.sessionId
                        },
                        body: JSON.stringify({
                          id: editEventGroupData.id,
                          name: editEventGroupData.name,
                          description: editEventGroupData.description,
                          physicalLocation: editEventGroupData.locationPhysical,
                          onlineLocation: editEventGroupData.locationOnline,
                          eventPrice: parseFloat(editEventGroupData.eventPrice) || 0,
                          price: parseFloat(editEventGroupData.price) || 0,
                          url: editEventGroupData.url
                        })
                      });
                      
                      if (response.ok) {
                        setEventMessage('Event series updated successfully');
                        setTimeout(() => setEventMessage(''), 3000);
                      } else {
                        const errorText = await response.text();
                        setEventMessage(`Error: ${errorText}`);
                      }
                    } catch (error) {
                      setEventMessage(`Error: ${error.message}`);
                    }
                  }} style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Update Event Series</button>
                </div>
              </div>
            )}
            <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #ccc' }}>
              <button type="submit">{editingEventId ? 'Update Event' : 'Create Event'}</button>
              <button type="button" onClick={() => { setShowEventForm(false); setEditingEventId(null); setActiveEventTab('event'); setEventFormData({ name: '', topic: '', startDateTime: '', endDateTime: '', description: '', locationPhysical: '', locationOnline: '', capacity: '', price: '', url: '', recordingUrl: '' }); setEventMessage(''); setEditEventGroupData(null); }} style={{ marginLeft: '1rem' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {showUserDialog && selectedUser && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                     backgroundColor: '#f0f8ff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: '600px', height: '600px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0', padding: '1rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc' }}>
            <h3 style={{ margin: '0', flex: 1, textAlign: 'center' }}>View Participant</h3>
            <button onClick={() => setShowUserDialog(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div style={{ padding: '2rem', flex: 1, overflow: 'auto', backgroundColor: '#e6f3ff' }}>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>First Name:</label>
              <input type="text" value={selectedUser.firstName || selectedUser.first_name || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Last Name:</label>
              <input type="text" value={selectedUser.lastName || selectedUser.last_name || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Email:</label>
              <input type="email" value={selectedUser.email || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Phone:</label>
              <input type="tel" value={selectedUser.phone || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Address 1:</label>
              <input type="text" value={selectedUser.address1 || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Address 2:</label>
              <input type="text" value={selectedUser.address2 || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>City:</label>
              <input type="text" value={selectedUser.city || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>State:</label>
              <input type="text" value={selectedUser.state || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>ZIP:</label>
              <input type="text" value={selectedUser.zip || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Title:</label>
              <input type="text" value={selectedUser.title || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Status:</label>
              <input type="text" value={selectedUser.status || ''} readOnly style={{ backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
              <label style={{ width: '50%', paddingTop: '0.25rem' }}>About:</label>
              <textarea value={selectedUser.about || ''} readOnly rows="3" style={{ width: '50%', resize: 'vertical', backgroundColor: '#f5f5f5' }} />
            </div>
            <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #ccc' }}>
              <button type="button" onClick={() => setShowUserDialog(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && eventToDelete && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                     backgroundColor: '#fff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: '400px', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
          <div style={{ padding: '1rem', backgroundColor: '#dc3545', color: 'white', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
            <h3 style={{ margin: '0', textAlign: 'center' }}>Confirm Delete</h3>
          </div>
          {deleteMessage && (
            <div style={{ padding: '0.75rem', margin: '1rem', borderRadius: '4px', backgroundColor: deleteMessage.includes('Error') || deleteMessage.includes('Failed') ? '#f8d7da' : '#d4edda', color: deleteMessage.includes('Error') || deleteMessage.includes('Failed') ? '#721c24' : '#155724', border: `1px solid ${deleteMessage.includes('Error') || deleteMessage.includes('Failed') ? '#f5c6cb' : '#c3e6cb'}` }}>
              {deleteMessage}
            </div>
          )}
          <div style={{ padding: '2rem' }}>
            <p style={{ margin: '0 0 1.5rem 0', textAlign: 'center' }}>
              Are you sure you want to delete the event <strong>{eventToDelete.event.name}</strong> on <strong>{eventToDelete.day.toLocaleDateString()}</strong> at <strong>{formatTime(eventToDelete.event.startDateTime)}</strong>?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
              <button onClick={confirmDeleteEvent} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '4px', cursor: 'pointer' }}>Yes, Delete</button>
              <button onClick={() => { setShowDeleteConfirm(false); setEventToDelete(null); setDeleteMessage(''); }} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showParticipantsDialog && selectedEventForParticipants && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                     backgroundColor: '#f0f8ff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: '600px', height: '500px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0', padding: '1rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc' }}>
            <h3 style={{ margin: '0', flex: 1, textAlign: 'center' }}>Event Participants</h3>
            <button onClick={() => setShowParticipantsDialog(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div style={{ padding: '1rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc', textAlign: 'center' }}>
            <strong>{selectedEventForParticipants.name}</strong> - {formatTime(selectedEventForParticipants.startDateTime)} - {formatTime(selectedEventForParticipants.endDateTime)}
          </div>
          <div style={{ padding: '1rem', flex: 1, overflow: 'auto', backgroundColor: '#e6f3ff' }}>
            {selectedEventForParticipants.participantList && selectedEventForParticipants.participantList.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>First Name</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Last Name</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Email</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Phone</th>
                    <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEventForParticipants.participantList.map((participant, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                      <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer', color: '#007bff' }} onClick={() => handleParticipantClick(participant.userId)}>{participant.firstName || ''}</td>
                      <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer', color: '#007bff' }} onClick={() => handleParticipantClick(participant.userId)}>{participant.lastName || ''}</td>
                      <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{participant.email || ''}</td>
                      <td style={{ padding: '0.5rem', border: '1px solid #ccc', whiteSpace: 'nowrap' }}>{participant.phone || ''}</td>
                      <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{participant.comment || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>No participants registered for this event</div>
            )}
          </div>
          <div style={{ padding: '1rem', textAlign: 'center', borderTop: '1px solid #ccc', backgroundColor: '#e6f3ff' }}>
            <button onClick={() => setShowParticipantsDialog(false)} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {showEventGroupForm && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                     backgroundColor: '#f0f8ff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: '600px', height: '600px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0', padding: '1rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc' }}>
            <h3 style={{ margin: '0', flex: 1, textAlign: 'center' }}>Create New Event Group</h3>
            <button onClick={() => setShowEventGroupForm(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          {eventGroupMessage && (
            <div style={{ padding: '0.75rem', margin: '1rem', borderRadius: '4px', backgroundColor: eventGroupMessage.includes('successfully') ? '#d4edda' : '#f8d7da', color: eventGroupMessage.includes('successfully') ? '#155724' : '#721c24', border: `1px solid ${eventGroupMessage.includes('successfully') ? '#c3e6cb' : '#f5c6cb'}` }}>
              {eventGroupMessage}
            </div>
          )}
          <div style={{ flex: 1, overflow: 'auto', backgroundColor: '#e6f3ff', padding: '2rem', paddingBottom: '0' }}>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Name:</label>
              <input type="text" value={eventGroupFormData.name} onChange={(e) => setEventGroupFormData({...eventGroupFormData, name: e.target.value})} required />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Topic:</label>
              <input type="text" value={eventGroupFormData.topic} onChange={(e) => setEventGroupFormData({...eventGroupFormData, topic: e.target.value})} required />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start' }}>
              <label style={{ width: '30%', paddingTop: '0.25rem' }}>Description:</label>
              <textarea value={eventGroupFormData.description} onChange={(e) => setEventGroupFormData({...eventGroupFormData, description: e.target.value})} rows="2" style={{ width: '70%', resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Days of Week:</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[{code: 'Su', name: 'Sunday'}, {code: 'Mo', name: 'Monday'}, {code: 'Tu', name: 'Tuesday'}, {code: 'We', name: 'Wednesday'}, {code: 'Th', name: 'Thursday'}, {code: 'Fr', name: 'Friday'}, {code: 'Sa', name: 'Saturday'}].map(day => (
                  <label key={day.code} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <input 
                      type="checkbox" 
                      checked={eventGroupFormData.daysOfWeek.includes(day.code)}
                      onChange={(e) => {
                        const newDays = e.target.checked 
                          ? [...eventGroupFormData.daysOfWeek, day.code]
                          : eventGroupFormData.daysOfWeek.filter(d => d !== day.code);
                        setEventGroupFormData({...eventGroupFormData, daysOfWeek: newDays});
                      }}
                    />
                    <span style={{ fontSize: '0.9rem' }}>{day.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Start Date:</label>
              <input type="date" value={eventGroupFormData.startDate} onChange={(e) => setEventGroupFormData({...eventGroupFormData, startDate: e.target.value})} required />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>End Date:</label>
              <input type="date" value={eventGroupFormData.endDate} onChange={(e) => setEventGroupFormData({...eventGroupFormData, endDate: e.target.value})} required />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Start Time:</label>
              <input type="time" value={eventGroupFormData.startTime} onChange={(e) => setEventGroupFormData({...eventGroupFormData, startTime: e.target.value})} required />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>End Time:</label>
              <input type="time" value={eventGroupFormData.endTime} onChange={(e) => setEventGroupFormData({...eventGroupFormData, endTime: e.target.value})} required />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Repeat Every:</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select value={eventGroupFormData.recurrenceInterval} onChange={(e) => setEventGroupFormData({...eventGroupFormData, recurrenceInterval: parseInt(e.target.value)})} required>
                  {[1,2,3,4,5,6,7,8,9,10].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <select value={eventGroupFormData.recurrenceType} onChange={(e) => setEventGroupFormData({...eventGroupFormData, recurrenceType: e.target.value})} required>
                  <option value="days">{eventGroupFormData.recurrenceInterval === 1 ? 'day' : 'days'}</option>
                  <option value="weeks">{eventGroupFormData.recurrenceInterval === 1 ? 'week' : 'weeks'}</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Occurrence Count:</label>
              <input type="number" value={eventGroupFormData.occurrenceCount} onChange={(e) => setEventGroupFormData({...eventGroupFormData, occurrenceCount: parseInt(e.target.value)})} min="1" required />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Capacity:</label>
              <input type="number" value={eventGroupFormData.capacity} onChange={(e) => setEventGroupFormData({...eventGroupFormData, capacity: e.target.value})} min="1" required />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Group Price:</label>
              <input type="number" value={eventGroupFormData.price} onChange={(e) => setEventGroupFormData({...eventGroupFormData, price: e.target.value})} min="0" step="0.01" placeholder="0.00" />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Individual Event Price:</label>
              <input type="number" value={eventGroupFormData.eventPrice} onChange={(e) => setEventGroupFormData({...eventGroupFormData, eventPrice: e.target.value})} min="0" step="0.01" placeholder="0.00" />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Physical Location:</label>
              <input type="text" value={eventGroupFormData.locationPhysical} onChange={(e) => setEventGroupFormData({...eventGroupFormData, locationPhysical: e.target.value})} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Online Location:</label>
              <input type="text" value={eventGroupFormData.locationOnline} onChange={(e) => setEventGroupFormData({...eventGroupFormData, locationOnline: e.target.value})} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>URL:</label>
              <input type="url" value={eventGroupFormData.url} onChange={(e) => setEventGroupFormData({...eventGroupFormData, url: e.target.value})} placeholder="https://" />
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#e9ecef' }}>
            <form onSubmit={handleEventGroupSubmit} style={{ display: 'inline' }}>
              <button type="submit">Create Event Group</button>
            </form>
            <button type="button" onClick={() => { setShowEventGroupForm(false); setEventGroupFormData({ name: '', description: '', daysOfWeek: [], recurrenceType: 'weeks', recurrenceInterval: 1, occurrenceCount: 10, startDate: '', endDate: '', startTime: '09:00', endTime: '11:00', price: '', eventPrice: '', capacity: '', topic: '', locationPhysical: '', locationOnline: '' }); setEventGroupMessage(''); }} style={{ marginLeft: '1rem' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroundSchool;